import React, { useCallback, useEffect } from 'react';
import { useModeFormStyle, inputStateSetterHelper, usePropValue, ModeInfoCompAutoCompleteInputField, ModeInfoCompSelectInputField }
    from '@moderepo/mode-app-components';
import { Entity, EntityMetricsDefinition, TimeSeriesAggregation } from '@moderepo/mode-apis';
import { RawDataIntervalType, AggregatedDataIntervalType, MetricsMetadata, MetricMetadata, TSDBDashboardWidgetDataSourceInputs, TSDBDataSourceType }
    from '../../containers';
import { TypeNone } from '../../utils/utils';
import { castMetricsMetadataInputs, TSDBDashboardWidgetSettingsFields } from './dashboardCompUtils';
import { MetricsMetadataInputsComp, MetricsMetadataInputsCompProps } from './MetricsMetadataInputsComp';
import { EntityInputComp } from '../entities';



export interface TSDBDashboardWidgetDataSourceInputsCompProps {

    // The input fields required for the data source input. This will be the input fields defined for
    // TSDBDashboardWidgetSettingsCompFields's dataSource
    readonly fields: {
    } & TSDBDashboardWidgetSettingsFields['dataSource'];

    // The localized texts needed for for the metadata input fields
    readonly texts: MetricsMetadataInputsCompProps['texts'];

    // The set of metadata that can be configured. Not all metadata are required for all widget. Some widget only required 'name', and 'unit'.
    // Some widget required threshold settings, etc.... The container needs to specify which metadata it wants to enable.
    readonly editableMetadata: readonly (keyof MetricMetadata)[];

    readonly isEditing?: boolean | undefined;
    readonly onFieldClicked?: (fieldName: string)=> void;

    // Handler for when 1 single data source input prop changed e.g. moduleId, or intervalType, etc... changed
    readonly onFormInputChange?: (fieldName: string, value: any)=> void | undefined;

    // Handler for when the entire data source input is updated e.g. moduleId, AND intervalType, etc...
    readonly onDataSourceInputsUpdated?: (dataSourceInputs: TSDBDashboardWidgetDataSourceInputs)=> void | undefined;
}



export const TSDBDashboardWidgetDataSourceInputsComp: React.FC<TSDBDashboardWidgetDataSourceInputsCompProps> = (
    props: TSDBDashboardWidgetDataSourceInputsCompProps,
) => {

    const [homeIdInput, setHomeIdInput] = usePropValue<number | undefined>(props.fields.homeId.value);
    const [dataSourceTypeInput, setDataSourceTypeInput] = usePropValue<TSDBDataSourceType | undefined>(
        props.fields.dataSourceType.value,
    );
    const [smartModuleIdInput, setSmartModuleIdInput] = usePropValue<string | undefined>(props.fields.smartModuleId?.value);
    const [seriesIdsInput, setSeriesIdsInput] = usePropValue<readonly string[] | undefined>(props.fields.seriesIds?.value);
    const [collectionIdInput, setCollectionIdInput] = usePropValue<string | undefined>(props.fields.collectionId?.value);

    const [entityInput, setEntityInput] = usePropValue<Entity | undefined>(props.fields.entity?.value);
    const [metricsDefinitionInput, setMetricsDefinitionInput] = usePropValue<EntityMetricsDefinition | undefined>(
        props.fields.metricsDefinition?.value,
    );

    const [valueNamesInput, setValueNamesInput] = usePropValue<readonly string[] | undefined>(props.fields.valueNames.value);
    const [tagNamesInput, setTagNamesInput] = usePropValue<readonly string[] | undefined>(props.fields.tagNames?.value);
    const [aggregationTypeInput, setAggregationTypeInput] = usePropValue<TimeSeriesAggregation | TypeNone | undefined>(
        props.fields.aggregationType.value,
    );
    const [intervalTypeInput, setIntervalTypeInput] = usePropValue<AggregatedDataIntervalType | RawDataIntervalType | undefined>(
        props.fields.intervalType.value,
    );
    const [metricsMetadataInput, setMetricsMetadataInput] = usePropValue<MetricsMetadata | undefined>(props.fields.metricsMetadata.value);

    const formClasses = useModeFormStyle();
    const { onDataSourceInputsUpdated } = props;


    /**
     * On input change handler for all the inputs. When an input change, call the onInputChangeHelper and pass it the setter functions
     * for all inputs. The helper function will call the setter function accordingly based on the input name.
     */
    const onInputChange = useCallback((inputName: string, value: any) => {

        // Update the local states
        inputStateSetterHelper(inputName, value, {
            homeId             : setHomeIdInput,
            dataSourceTypeInput: setDataSourceTypeInput,
            smartModuleId      : setSmartModuleIdInput,
            seriesIds          : setSeriesIdsInput,
            collectionId       : setCollectionIdInput,
            entity             : setEntityInput,
            metricsDefinition  : setMetricsDefinitionInput,
            valueNames         : setValueNamesInput,
            tagNames           : setTagNamesInput,
            aggregationType    : setAggregationTypeInput,
            intervalType       : setIntervalTypeInput,
        });
    }, [setHomeIdInput, setDataSourceTypeInput, setSmartModuleIdInput, setSeriesIdsInput, setCollectionIdInput, setEntityInput,
        setMetricsDefinitionInput, setValueNamesInput, setTagNamesInput, setAggregationTypeInput, setIntervalTypeInput]);



    /**
     * This useEffect is used for notifying the container that the data source inputs changed. Unlike the onInputChange handler which the handler
     * only get the name of the input that changed and the new value for that 1 input. This `onDataSourceInputsChange` handler will get the
     * entire data source input object with all the latest values.
     */
    useEffect(() => {
        if (onDataSourceInputsUpdated) {
            const castedMetricsMetadataInput = castMetricsMetadataInputs(metricsMetadataInput);

            // Pass all the inputs' value for the data source settings to the handler
            onDataSourceInputsUpdated(
                {
                    homeId           : homeIdInput ? Number(homeIdInput) : undefined,
                    dataSourceType   : dataSourceTypeInput,
                    smartModuleId    : smartModuleIdInput?.trim(),
                    seriesIds        : seriesIdsInput,
                    collectionId     : collectionIdInput?.trim(),
                    entity           : entityInput,
                    metricsDefinition: metricsDefinitionInput,
                    valueNames       : valueNamesInput,
                    tagNames         : tagNamesInput,
                    metricsMetadata  : castedMetricsMetadataInput,
                    aggregationType  : aggregationTypeInput,
                    intervalType     : intervalTypeInput,
                },
            );
        }
    }, [aggregationTypeInput, collectionIdInput, dataSourceTypeInput, entityInput, homeIdInput, intervalTypeInput, metricsDefinitionInput,
        metricsMetadataInput, onDataSourceInputsUpdated, seriesIdsInput, smartModuleIdInput, tagNamesInput, valueNamesInput]);


    /**
     * on metrics metadata inputs updated
     */
    const onMetricsMetadataInputChange = useCallback((updatedMetadataInputs: MetricsMetadata) => {
        if (metricsMetadataInput !== updatedMetadataInputs) {
            // Save the inputs locally
            setMetricsMetadataInput(updatedMetadataInputs);

            // notify the container that the `metricsMetadata` input is updated
            if (props.onFormInputChange) {
                props.onFormInputChange('metricsMetadata', updatedMetadataInputs);
            }
        }
    }, [metricsMetadataInput, props, setMetricsMetadataInput]);


    return (
        <>
            {props.fields.homeId && (
                <ModeInfoCompSelectInputField
                    className={formClasses.formInputField}
                    compProps={props}
                    inputProps={props.fields.homeId}
                    fieldName="homeId"
                    inputValue={homeIdInput}
                    onInputChange={onInputChange}
                />
            )}
            {props.fields.dataSourceType && (
                <ModeInfoCompSelectInputField
                    className={formClasses.formInputField}
                    compProps={props}
                    inputProps={props.fields.dataSourceType}
                    fieldName="dataSourceType"
                    inputValue={dataSourceTypeInput}
                    onInputChange={onInputChange}
                />
            )}
            {props.fields.smartModuleId && (
                <ModeInfoCompSelectInputField
                    className={formClasses.formInputField}
                    compProps={props}
                    inputProps={props.fields.smartModuleId}
                    fieldName="smartModuleId"
                    inputValue={smartModuleIdInput}
                    onInputChange={onInputChange}
                />
            )}
            {props.fields.seriesIds && (
                <ModeInfoCompAutoCompleteInputField
                    className={formClasses.formInputField}
                    fieldName="seriesIds"
                    compProps={props}
                    inputProps={props.fields.seriesIds}
                    inputValue={seriesIdsInput}
                    onInputChange={onInputChange}
                />
            )}
            {props.fields.collectionId && (
                <ModeInfoCompSelectInputField
                    className={formClasses.formInputField}
                    compProps={props}
                    inputProps={props.fields.collectionId}
                    fieldName="collectionId"
                    inputValue={collectionIdInput}
                    onInputChange={onInputChange}
                />
            )}
            {props.fields.entity && (
                <EntityInputComp
                    className={formClasses.formInputField}
                    fieldName="entity"
                    isEditing={props.isEditing}
                    {...props.fields.entity}
                    value={entityInput}
                    onFieldClicked={props.onFieldClicked}
                    onFormInputChange={(fieldName: string, entity: Entity | undefined) => {
                        // Save the input locally
                        onInputChange(fieldName, entity);

                        // notify the container input changed
                        if (props.onFormInputChange) {
                            props.onFormInputChange(fieldName, entity);
                        }
                    }}
                />
            )}
            {props.fields.metricsDefinition && (
                <ModeInfoCompSelectInputField
                    className={formClasses.formInputField}
                    compProps={props}
                    inputProps={props.fields.metricsDefinition}
                    fieldName="metricsDefinition"
                    inputValue={metricsDefinitionInput}
                    onInputChange={onInputChange}
                />
            )}
            {props.fields.valueNames && (
                <ModeInfoCompAutoCompleteInputField
                    className={formClasses.formInputField}
                    fieldName="valueNames"
                    compProps={props}
                    inputProps={props.fields.valueNames}
                    inputValue={valueNamesInput}
                    onInputChange={onInputChange}
                />
            )}
            {props.fields.tagNames && (
                <ModeInfoCompAutoCompleteInputField
                    className={formClasses.formInputField}
                    fieldName="tagNames"
                    compProps={props}
                    inputProps={props.fields.tagNames}
                    inputValue={tagNamesInput}
                    onInputChange={onInputChange}
                />
            )}
            {props.fields.aggregationType && (
                <ModeInfoCompSelectInputField
                    className={formClasses.formInputField}
                    compProps={props}
                    inputProps={props.fields.aggregationType}
                    fieldName="aggregationType"
                    inputValue={aggregationTypeInput}
                    onInputChange={onInputChange}
                />
            )}
            {props.fields.intervalType && (
                <ModeInfoCompSelectInputField
                    className={formClasses.formInputField}
                    compProps={props}
                    inputProps={props.fields.intervalType}
                    fieldName="intervalType"
                    inputValue={intervalTypeInput}
                    onInputChange={onInputChange}
                />
            )}

            {/* Use MetricsMetadataInputsComp for the metrics' metadata inputs */}
            <MetricsMetadataInputsComp
                isEditing={props.isEditing}
                fields={props.fields}
                texts={props.texts}
                editableMetadata={props.editableMetadata}
                onFieldClicked={props.onFieldClicked}   // onFieldClicked, propagate it to the container
                onInputChanged={onMetricsMetadataInputChange}
            />
        </>
    );
};
